/**
 * @fileoverview Game Controls - 統一コントロール関数群
 */

import { GameStatus } from '../core/constants.js';
import gameState from '../core/game-state.js';
import { saveEntry, getTop, isHighScore } from '../core/highscore.js';
import { MAX_HIGHSCORE_ENTRIES } from '../core/persistence-manager.js';
import { DEFAULT_LIMIT_VALUES } from '../shared/constants/limit-defaults.js';
import { STATE_PATHS } from '../shared/constants/state-paths.js';

import { clearActivationQueue } from './activation-queue.js';
import Board from './board.js';
import * as moveLimitDriver from './limit-system/move-limit.js';
import * as timeLimitDriver from './limit-system/time-limit.js';
import { initializePieces } from './pieces.js';
import { getScore, resetScore, getSessionStats } from './scoring.js';

// ゲーム用の単一Boardインスタンスを作成
let gameBoard = null;

// ========================================
// ドライバ管理（一元管理）
// ========================================
let currentLimitDriver = null;
const initializedLimitDriverVisibility = new Set();

/**
 * アクティブなドライバを取得（UI層から参照）
 */
function getActiveLimitDriver() {
  return currentLimitDriver;
}

/**
 * ドライバ初期化（再初期化対応）
 */
function initializeLimitDriver(gameState) {
  const settings = gameState.getState(STATE_PATHS.SETTINGS);
  const mode = settings?.gameMode === 'Classic' ? 'Classic' : 'Blitz';

  if (currentLimitDriver && currentLimitDriver.stop) {
    currentLimitDriver.stop(gameState);
  }

  currentLimitDriver = mode === 'Classic' ? moveLimitDriver : timeLimitDriver;

  if (
    !initializedLimitDriverVisibility.has(mode) &&
    currentLimitDriver.initializeVisibilityHandling
  ) {
    currentLimitDriver.initializeVisibilityHandling();
    initializedLimitDriverVisibility.add(mode);
  }

  return currentLimitDriver;
}

// Pause 操作の内部状態（timer.js のローカル変数パターンと同じ）
let pausedFromStatus = null;

function shouldSkipInitialBoardSeedForTutorial() {
  const tutorialState = gameState.getState(STATE_PATHS.UI_TUTORIAL) || {};
  return tutorialState.status === 'running' && tutorialState.step === 'start';
}

/**
 * ゲームボードインスタンスを取得または作成
 * @returns {Board} ゲームボードインスタンス
 */
function getGameBoard() {
  if (!gameBoard) {
    gameBoard = new Board();
  }
  return gameBoard;
}

/**
 * スキル使用回数設定のラッパー関数
 * @param {number} uses - スキル使用回数
 */
function setSkillUses(uses) {
  const skillUses = Math.max(0, uses | 0);
  const currentTargeting = gameState.getState(STATE_PATHS.UI_IS_SKILL_TARGETING);
  gameState.setState(STATE_PATHS.UI_IS_SKILL_TARGETING, currentTargeting, 'skillUsesUpdate');
  gameState.setState(STATE_PATHS.UI_REMAINING_SKILL_USES, skillUses, 'skillUsesUpdate');
}

/**
 * Pause前の元の状態を取得
 * Settings画面などでMenu開く前の状態を判定するために使用
 * @returns {string|null} Pause前のGameStatus、またはnull
 */
function getPausedFromStatus() {
  return pausedFromStatus;
}

export {
  gameStart,
  gameOver,
  exitSkillTargeting,
  getGameBoard,
  setSkillUses,
  getActiveLimitDriver,
  resetToTitle,
  restartGame,
  pauseGame,
  resumeGame,
  getPausedFromStatus,
};

// 内部ヘルパー関数
function setStatus(status) {
  gameState.setState(STATE_PATHS.CORE_STATUS, status);
  // 状態変更はGameState購読で追跡
}

/**
 * スキルハンドラーのクリーンアップ
 */
function cleanupSkillHandlers() {
  // SkillSystemのクリーンアップは各インスタンスが自己管理
}

function exitSkillTargeting() {
  const currentUIState = gameState.getState(STATE_PATHS.UI);
  if (!currentUIState.isSkillTargeting) return;

  // スキル状態をGameState経由で更新
  gameState.setState(STATE_PATHS.UI_IS_SKILL_TARGETING, false, 'exitSkillTargeting');
  gameState.setState(
    STATE_PATHS.UI_REMAINING_SKILL_USES,
    currentUIState.remainingSkillUses,
    'exitSkillTargeting',
  );

  // controls状態とリスナーをクリア
  cleanupSkillHandlers();

  // ボタン状態を明示的に更新（購読ではなく直接呼び出し）
  import('../presentation/controllers/button-controller.js')
    .then(({ updateAllButtons }) => {
      updateAllButtons(gameState);
    })
    .catch(() => {
      // ボタン更新失敗は非クリティカル
    });
}

/**
 * ゲームを一時停止する
 * Paused状態への遷移前に必要なクリーンアップを自動実行（ターゲティング解除など）
 * 多重呼び出しは無害（idempotent）
 */
function pauseGame() {
  const currentStatus = gameState.getState(STATE_PATHS.CORE_STATUS);

  // 既に Paused の場合は何もしない（idempotent）
  if (currentStatus === GameStatus.Paused) {
    return;
  }

  // Paused状態の前提条件を満たす
  const ui = gameState.getState(STATE_PATHS.UI);
  if (ui.isSkillTargeting) {
    exitSkillTargeting();
  }

  // 現在の状態を保存
  pausedFromStatus = currentStatus;

  // Paused 状態に遷移
  setStatus(GameStatus.Paused);
}

/**
 * ゲームを再開する
 * 保存された状態に復元。多重呼び出しは無害（idempotent）
 */
function resumeGame() {
  const currentStatus = gameState.getState(STATE_PATHS.CORE_STATUS);

  // Paused 状態でない場合は何もしない（idempotent）
  if (currentStatus !== GameStatus.Paused) {
    return;
  }

  // 保存された状態がない場合は何もしない（防御）
  if (pausedFromStatus === null) {
    return;
  }

  // 保存された状態に復元
  setStatus(pausedFromStatus);
  pausedFromStatus = null;
}

async function gameStart() {
  // Title状態からの開始を許可

  // 重複処理チェック
  const currentStatus = gameState.getState(STATE_PATHS.CORE_STATUS);
  if (currentStatus !== GameStatus.Title) {
    return; // Title状態でない場合はスキップ
  }

  // 前回のゲームの結果表示を非表示
  try {
    const { hideResultHUD } = await import('../presentation/screens/result-screen.js');
    hideResultHUD();
  } catch (error) {
    // 結果表示の非表示失敗は非クリティカル
  }

  // 開始時に即座に処理状態を設定
  setStatus(GameStatus.Processing);

  try {
    // 新しいゲーム用にスコアをリセット
    resetScore();

    // Post-Settle Activation Queueをクリア
    clearActivationQueue();

    // ボタン状態はUI層で自動計算

    // このプレイセッション用にスキル使用回数を初期化
    setSkillUses(1);
    // 統計更新: プレイ回数
    const currentCount = gameState.getState(STATE_PATHS.STATS)?.playCount || 0;
    gameState.setState(STATE_PATHS.STATS_PLAY_COUNT, Number(currentCount + 1) | 0);

    // ドライバ初期化（毎回初期化）
    const driver = initializeLimitDriver(gameState);

    // モードに応じた設定
    const settings = gameState.getState(STATE_PATHS.SETTINGS);
    const mode = settings?.gameMode || 'Blitz';

    if (mode === 'Classic') {
      driver.configure(gameState, { maxMoves: DEFAULT_LIMIT_VALUES.MAX_MOVES });
    } else {
      driver.configure(gameState, { timeLimitMs: DEFAULT_LIMIT_VALUES.TIME_LIMIT_MS });
    }

    // コールバック単一化（LIMIT_ON_LIMIT_REACHED）
    gameState.setState(STATE_PATHS.LIMIT_ON_LIMIT_REACHED, gameOver);

    // 開始（reset統合済み、冪等性保証）
    driver.start(gameState);

    // 盤面初期化を実行
    try {
      const { resetBoard } = await import('../shared/animations-public.js');
      await resetBoard();
    } catch (error) {
      // ボードリセット失敗は非クリティカル
    }
    const board = getGameBoard();
    const skipInitialBoardSeed = shouldSkipInitialBoardSeedForTutorial();

    // マッチゼロ初期化（カスケード不要）
    if (!skipInitialBoardSeed) {
      await initializePieces(board, { mode: 'noMatches' });
    } else {
      // Tutorial will immediately overwrite the board; skip redundant drop.
      board.clear();
    }

    // ゲーム開始完了
  } finally {
    // ゲーム開始操作完了後にready状態にリセット
    setStatus(GameStatus.Ready);
  }
}

async function handleGameOverAnimation(_currentScore) {
  // ピースアニメーション開始前にGameOver状態に遷移
  setStatus(GameStatus.GameOver);

  // ピース排出アニメーション
  const board = getGameBoard();
  board.clear();

  // アニメーション実行
  const { ejectAllPieces } = await import('../graphics/animation/result-animations.js');
  await ejectAllPieces();
}

async function handleHighScoreSave(currentScore) {
  // ベストスコアを計算（統計値と履歴の双方から取得）
  const top = getTop(1);
  const prevBestFromHistory = top && top[0] ? Number(top[0].score) || 0 : 0;
  const statsSnapshot = gameState.getState(STATE_PATHS.STATS) || {};
  const prevBestFromStats = Number(statsSnapshot.bestScore) || 0;
  const prevBest = Math.max(prevBestFromHistory, prevBestFromStats);

  // スコア表示
  setTimeout(async () => {
    const { showResultHUD } = await import('../presentation/screens/result-screen.js');
    showResultHUD({
      score: currentScore,
      bestScore: Math.max(currentScore, prevBest),
      isNewHighScore: currentScore > prevBest,
    });
  }, 1000);

  // ハイスコアを自動保存
  if (isHighScore(currentScore, MAX_HIGHSCORE_ENTRIES)) {
    const name = 'Player';
    saveEntry({ name, score: currentScore });
  }

  // 統計を更新
  updateStats(currentScore);
}

function updateStats(currentScore) {
  const stats = gameState.getState(STATE_PATHS.STATS);
  const { maxChain, maxMatch } = getSessionStats();

  // 4項目を更新
  gameState.setState(STATE_PATHS.STATS_PLAY_COUNT, (stats.playCount || 0) + 1);
  gameState.setState(STATE_PATHS.STATS_BEST_SCORE, Math.max(currentScore, stats.bestScore || 0));
  gameState.setState(STATE_PATHS.STATS_LONGEST_CHAIN, Math.max(maxChain, stats.longestChain || 0));
  gameState.setState(STATE_PATHS.STATS_BEST_MATCH, Math.max(maxMatch, stats.bestMatch || 0));
}

function handleGameOverCleanup() {
  // アニメーション完了後にTitle状態に遷移
  setStatus(GameStatus.Title);

  // 統一システムでスキル状態をリセット
  setSkillUses(0);

  // ボタン状態をGameState経由で管理
  // 注: ドライバはstop()で既にゼロ化済み、reset()は不要
}

async function gameOver() {
  // 再入防止: Ready状態でない場合は無視
  const currentStatus = gameState.getState(STATE_PATHS.CORE_STATUS);
  if (currentStatus !== GameStatus.Ready) return;

  // ゲームオーバーシーケンス中はユーザー入力をブロック

  try {
    // 最初にスコアを事前計算
    const currentScore = getScore();

    // ドライバ停止（抽象経由）
    if (currentLimitDriver && currentLimitDriver.stop) {
      currentLimitDriver.stop(gameState);
    }

    // ハイスコア処理を実行
    handleHighScoreSave(currentScore);

    // ゲームオーバーアニメーションとピース排出を実行
    await handleGameOverAnimation(currentScore);

    // 最終クリーンアップと状態遷移を実行
    handleGameOverCleanup();
  } finally {
    // 常に入力のブロックを解除
  }
}

/**
 * アクティブセッションを安全に中断して Title 状態へ戻す
 * - Ready / Processing / Paused / GameOver から呼び出し可能
 * - Title 状態からの呼び出しは no-op
 * - エラーが起きても Title まで戻ることを保証
 *
 * 注意: Processing 中（Cascade進行中）に呼び出した場合、
 * 非同期処理の完了を待たずに状態をリセットするため、
 * 予期しない動作が発生する可能性がある。
 * 将来的には AbortSignal などでの中断処理を検討。
 */
async function resetToTitle() {
  const currentStatus = gameState.getState(STATE_PATHS.CORE_STATUS);

  // Title 状態からの呼び出しは no-op
  if (currentStatus === GameStatus.Title) {
    return;
  }

  try {
    // 1. 即座に Processing に遷移（入力ブロック）
    setStatus(GameStatus.Processing);

    // 2. スコアをリセット
    resetScore();

    // 3. ドライバ停止
    if (currentLimitDriver && currentLimitDriver.stop) {
      currentLimitDriver.stop(gameState);
    }

    // 4. スキルターゲティング解除
    exitSkillTargeting();

    // 5. Post-Settle Activation Queue をクリア
    clearActivationQueue();

    // 6. 結果HUD を非表示
    try {
      const { hideResultHUD } = await import('../presentation/screens/result-screen.js');
      hideResultHUD();
    } catch (error) {
      // 結果表示の非表示失敗は非クリティカル
    }

    // 7. ボードクリア & リセット
    const board = getGameBoard();
    board.clear();
    try {
      const { resetBoard } = await import('../shared/animations-public.js');
      await resetBoard();
    } catch (error) {
      // ボードリセット失敗は非クリティカル
    }

    // 8. スキル弾数リセット（GameOver と同じ処理）
    setSkillUses(0);

    // 9. Pause 状態のクリア
    pausedFromStatus = null;

    // 注: ドライバはstop()で既にゼロ化済み、reset()は不要
  } finally {
    // 必ず Title 状態へ戻す（エラー時も保証）
    // ボタン状態は button-controller が自動的に更新する
    setStatus(GameStatus.Title);
  }
}

/**
 * ゲームをリスタートする（Title に戻してから即座に開始）
 * 通常は resetToTitle() を使い、ユーザーが Start を押すまで待つ推奨。
 * この関数は自動再開が必要な特殊ケース（チュートリアルなど）用。
 */
async function restartGame() {
  await resetToTitle();
  await gameStart();
}
